<template>
  <div v-if="!isLoading">
    <div class="mb-4">
      <h2 class="mb-1h" :style="color ? { color } : {}">
        {{ $t('consumables.waterFilling.header') }}
      </h2>
      <div class="consumption-summary mb-2">
        <span class="large-text">{{ round(Number(waterFilling?.weekConsumption ?? 0), 1) }}</span>
        <span class="unit-text">m³ / {{ $t('consumables.waterFilling.last7days') }}</span>
      </div>
      <ConsumptionHistoryLabels
        v-if="waterFilling?.dataSeries?.length"
        :data-series="waterFilling.dataSeries"
        :color="color"
      />
    </div>

    <div class="grid-wrapper">
      <div class="grid">
        <div class="icon">
          <Icon icon="aip-water-filling" :style="{ color }" size="3rem" />
        </div>
        <p>{{ $t('consumables.waterFilling.totalTimeFromLastReset') }}</p>
        <div class="large">{{ waterFilling?.totalTimeFromLastReset }}</div>
      </div>
      <div class="grid">
        <div class="icon" />
        <p>{{ $t('consumables.waterFilling.totalConsumption') }}</p>
        <div class="large">{{ litersToM3(waterFilling?.totalLitersFromLastReset ?? 0).toFixed(2) }}</div>
      </div>
      <div class="last-reset-grid">
        <p>{{ $t('consumables.waterFilling.lastReset') }}</p>
        <p>{{ lastResetTime }}</p>
        <p>{{ lastResetDate }}</p>
      </div>
    </div>

    <div class="flex justify-end mb-1h mt-2h">
      <DialogComponent
        v-model="waterFillingResetDialog"
      >
        <template #activator>
          <BtnComponent
            white
            style="width: calc(40% + 1rem)"
            @click="waterFillingResetDialog = true"
          >
            {{ $t('common.reset') }}
          </BtnComponent>
        </template>
        <template #default>
          <h2>{{ $t('consumables.waterFilling.confirmReset') }}</h2>
          <p class="mb-5">{{ $t('consumables.waterFilling.confirmResetMessage') }}</p>
          <BtnComponent
            primary
            block
            large
            class="mb-1"
            :loading="isLoadingReset"
            :disabled="isLoadingReset"
            @click="resetWaterFilling"
          >
            {{ $t('common.reset') }}
          </BtnComponent>
          <BtnComponent
            block
            outline
            large
            @click="waterFillingResetDialog = false"
          >
            {{ $t('common.cancel') }}
          </BtnComponent>
        </template>
      </DialogComponent>
    </div>

    <hr class="mb-2h">

    <h3>{{ $t('consumables.waterFilling.configuration') }}</h3>
    <div class="flex align-center mb-1h">
      <h4 class="grow-1">{{ $t('consumables.waterFilling.fillingSpeed') }}</h4>
      <SelectComponent
        v-model="updateItem.litersPerMinute"
        :options="fillingSpeedOptions"
        :disabled="savingConfiguration"
        class="mb-0 mt-0"
        style="width: calc(40% + 1rem)"
      >
        <template #append>
          l/min
        </template>
      </SelectComponent>
    </div>
    <div class="flex align-center justify-end mb-1h">
      <BtnComponent
        white
        style="width: calc(40% + 1rem)"
        :loading="savingConfiguration"
        @click="updateWaterFillingSpeed"
      >
        {{ $t('consumables.waterFilling.saveConfiguration') }}
      </BtnComponent>
    </div>
  </div>
  <div v-else>
    <BtnComponent loading block disabled large />
  </div>
</template>

<script>
import { ViewQuery, UpdateWaterFillingSpeedMutation, ResetWaterFillingCounterMutation } from '@acs/components-library/src/gql/live/ConsumableWaterFillingDetail.js';
import { isDemo } from '@acs/components-library/src/demo.js';
import SelectComponent from '@acs/components-library/src/components/common/Select.vue';
import BtnComponent from '@acs/components-library/src/components/common/Btn.vue';
import DialogComponent from '@acs/components-library/src/components/common/Dialog.vue';
import ConsumptionHistoryLabels from '@acs/components-library/src/components/deviceDetail/diagrams/ConsumptionHistoryLabels.vue';
import Icon from '@acs/components-library/src/components/common/Icon.vue';
import formatGrammaticalNumber from '@acs/components-library/src/helpers/gramaticalNumber.js';
import { useToastStore } from '../store/toastStore.js';
import { useMath } from '@acs/components-library/src/composables/Math.js';

export default {
  name: 'ConsumableWaterFillingDetail',
  components: {
    BtnComponent,
    SelectComponent,
    DialogComponent,
    ConsumptionHistoryLabels,
    Icon,
  },
  setup() {
    const toastStore = useToastStore();
    const { round, litersToM3 } = useMath();
    return { toastStore, round, litersToM3 };
  },
  data() {
    return {
      sn: this.$route.params.sn,
      isDemo: isDemo(),
      isLoadingReset: false,
      waterFillingResetDialog: false,
      savingConfiguration: false,
      updateItem: {
        litersPerMinute: null,
      },
      fillingSpeedOptions: Array
        .from({ length: 100 }, (v, k) => k)
        .map((v) => ({
          value: v,
          text: v,
        })),
    };
  },
  apollo: {
    unitBySerialNumber: {
      query: ViewQuery,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          sn: this.sn,
        };
      },
    },
  },
  computed: {
    unit() {
      return this.unitBySerialNumber;
    },
    waterFilling() {
      return this.unit?.consumables?.find((c) => c?.__typename === 'WaterFillingConsumable') ?? null;
    },
    color() {
      return this.waterFilling?.color;
    },
    lastResetRaw() {
      return this.waterFilling?.lastReset ?? null;
    },
    lastResetTime() {
      if (!this.lastResetRaw || this.lastResetRaw === '---') return '---';
      return String(this.lastResetRaw).split(' ')[0] ?? '---';
    },
    lastResetDate() {
      if (!this.lastResetRaw || this.lastResetRaw === '---') return '---';
      const parts = String(this.lastResetRaw).split(' ');
      return parts.slice(1).join(' ') || '---';
    },
    isLoading() {
      return this.$apollo.loading || !this.unit;
    },
  },
  watch: {
    waterFilling: {
      immediate: true,
      handler(v) {
        if (v && v.litersPerMinute !== null && v.litersPerMinute !== undefined) {
          this.updateItem.litersPerMinute = v.litersPerMinute;
        }
      },
    },
  },
  methods: {
    formatGrammaticalNumber,
    async resetWaterFilling() {
      if (this.isDemo) {
        this.toastStore.showSuccess(this.$t('consumables.waterFilling.counterReset'));
        return;
      }

      this.isLoadingReset = true;
      const { data: { resetWaterFillingCounter } } = await this.$apollo.mutate({
        mutation: ResetWaterFillingCounterMutation,
        variables: {
          serialNumber: this.sn,
        },
      }).finally(() => {
        this.isLoadingReset = false;
        this.waterFillingResetDialog = false;
      });

      // todo use common error message
      switch (resetWaterFillingCounter.__typename) {
        case 'WaterFilling': // 🥳
          this.toastStore.showSuccess(this.$t('consumables.waterFilling.counterReset'));
          await this.$apollo.queries.unitBySerialNumber.refetch();
          break;
        case 'WaterFillingNotAllowedError':
          this.toastStore.showError(this.$t('consumables.waterFilling.notAllowed'));
          break;
        case 'UnitNotPairedError':
          this.toastStore.showError(this.$t('unit.unitNotPaired'));
          break;
        case 'UnitNotFoundError':
          this.toastStore.showError(this.$t('unit.unitNotFound'));
          break;
        default:
          this.toastStore.showError(this.$t('common.error.unknownResponseType', { type: resetWaterFillingCounter.__typename }));
          break;
      }
    },
    async updateWaterFillingSpeed() {
      if (this.isDemo) {
        this.toastStore.showSuccess(this.$t('common.successfullySaved'));
        return;
      }

      this.savingConfiguration = true;
      const litersPerMinute = Number(this.updateItem.litersPerMinute);
      const { data: { updateWaterFillingSpeed } } = await this.$apollo.mutate({
        mutation: UpdateWaterFillingSpeedMutation,
        variables: {
          serialNumber: this.sn,
          litersPerMinute,
        },
      }).finally(() => {
        this.savingConfiguration = false;
      });

      // todo use common error message
      switch (updateWaterFillingSpeed.__typename) {
        case 'WaterFilling': // 🥳
          this.toastStore.showSuccess(this.$t('common.successfullySaved'));
          break;
        case 'WaterFillingNotAllowedError':
          this.toastStore.showError(this.$t('consumables.waterFilling.notAllowed'));
          break;
        case 'UnitNotFoundError':
          this.toastStore.showError(this.$t('unit.unitNotFound'));
          break;
        case 'UnitNotPairedError':
          this.toastStore.showError(this.$t('unit.unitNotPaired'));
          break;
        default:
          this.toastStore.showError(this.$t('common.error.unknownResponseType', { type: updateWaterFillingSpeed.__typename }));
          break;
      }

    },
  },
};
</script>

<style lang="scss" scoped>
.consumption-summary {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.grid-wrapper {
  padding-top: 1rem;
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 1rem;
}

.grid {
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: 2fr 3fr 2fr;
  row-gap: 0.5rem;
  column-gap: 2rem;
  align-items: center;
  justify-items: center;

  > :nth-child(3n+2) { justify-self: start; }
  > :nth-child(3n) { justify-self: end; }
}

.last-reset-grid {
  display: grid;
  grid-template-columns: 2fr 2fr 2fr;
  row-gap: 0.5rem;
  column-gap: 2rem;
  align-items: start;
  justify-items: end;
}

.icon {
  width: 5rem;
  height: 5rem;
  display: grid;
  place-items: center;
}

.large {
  font-size: 2rem;
  line-height: 2rem;
}

.large-text {
  font-size: 1.5rem;
}

.unit-text {
  font-size: 1rem;
}
</style>
